import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Loader2, Crown } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useClaimPlace } from '@/hooks/usePetPlaces';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  placeId: string | null;
  placeName?: string;
}

export default function ClaimPlaceDialog({ open, onOpenChange, placeId, placeName }: Props) {
  const { t } = useTranslation();
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [instagram, setInstagram] = useState('');
  const { mutateAsync, isPending } = useClaimPlace();

  useEffect(() => {
    if (open) {
      setPhone('');
      setEmail('');
      setWebsite('');
      setInstagram('');
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!placeId) return;
    const contact: Record<string, string> = {};
    if (phone.trim()) contact.phone = phone.trim();
    if (email.trim()) contact.email = email.trim();
    if (website.trim()) contact.website = website.trim();
    if (instagram.trim()) contact.instagram = instagram.trim();

    if (Object.keys(contact).length === 0) {
      toast({
        title: t('places.errors.contactRequired', 'Please add at least one contact method'),
        variant: 'destructive',
      });
      return;
    }
    try {
      await mutateAsync({ placeId, contact });
      toast({
        title: t('places.claim.success', 'Place claimed!'),
        description: t('places.claim.successDesc', 'Your place now shows a premium badge.'),
      });
      onOpenChange(false);
    } catch (e: any) {
      toast({ title: t('common.error', 'Error'), description: e.message, variant: 'destructive' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-amber-500" />
            {t('places.claim.title', 'Claim this business')}
          </DialogTitle>
          <DialogDescription>
            {placeName && <span className="font-medium">{placeName}</span>}
            <br />
            {t(
              'places.claim.desc',
              'Free claim. Get a golden pin, contact info, photo and hours on your listing.'
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>{t('places.contact.phone', 'Phone')}</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={40} />
          </div>
          <div>
            <Label>{t('places.contact.email', 'Email')}</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} maxLength={120} />
          </div>
          <div>
            <Label>{t('places.contact.website', 'Website')}</Label>
            <Input
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              maxLength={200}
              placeholder="https://..."
            />
          </div>
          <div>
            <Label>{t('places.contact.instagram', 'Instagram')}</Label>
            <Input value={instagram} onChange={(e) => setInstagram(e.target.value)} maxLength={80} />
          </div>
          <Button className="w-full" onClick={handleSubmit} disabled={isPending}>
            {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {t('places.claim.submit', 'Claim it now')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
